'use client'

import ConfirmModal from '@/components/confirm-modal'
import {
    SignDocument,
    deleteSignDocument,
    getSignDocuments,
} from '@/lib/api/administrative/sign-api'
import { ApiResponsePagination } from '@/lib/api/request'
import { useResourcePermissions } from '@/lib/hooks/use-resource-permissions'
import { useAuth } from '@/store/hooks'
import { format } from 'date-fns'
import { ptBR } from 'date-fns/locale'
import { useEffect, useState } from 'react'
import {
    MdAdd,
    MdArrowBack,
    MdArrowForward,
    MdDelete,
    MdDraw,
} from 'react-icons/md'
import { useNavigate } from 'react-router-dom'
import { toast } from 'react-toastify'

const STATUS_LABELS: Record<string, { label: string; className: string }> = {
    PENDING: { label: 'Pendente', className: 'badge-ghost' },
    SENT: { label: 'Enviado', className: 'badge-info' },
    SIGNING: { label: 'Assinando', className: 'badge-warning' },
    COMPLETED: { label: 'Concluído', className: 'badge-success' },
    CANCELLED: { label: 'Cancelado', className: 'badge-error' },
}

const STATUS_FILTER_OPTIONS = [
    { value: '', label: 'Todos' },
    { value: 'PENDING', label: 'Pendente' },
    { value: 'SENT', label: 'Enviado' },
    { value: 'SIGNING', label: 'Assinando' },
    { value: 'COMPLETED', label: 'Concluído' },
    { value: 'CANCELLED', label: 'Cancelado' },
]

const ORIGIN_LABELS: Record<string, string> = {
    CONTRACT: 'Contrato',
}

function getOriginLabel(origin?: string | null) {
    if (!origin) return 'Avulso'
    return ORIGIN_LABELS[origin] || origin
}

export default function SignDocumentsListPage() {
    const navigate = useNavigate()
    const { user } = useAuth()
    const { canCreate, canDelete } = useResourcePermissions('administrative', 'sign')

    const [documents, setDocuments] = useState<SignDocument[]>([])
    const [loading, setLoading] = useState(true)
    const [pagination, setPagination] = useState<ApiResponsePagination | null>(
        null,
    )
    const [total, setTotal] = useState(0)
    const [page, setPage] = useState(1)
    const [statusFilter, setStatusFilter] = useState('')
    const [deleteModalId, setDeleteModalId] = useState<number | null>(null)
    const [deleting, setDeleting] = useState(false)

    useEffect(() => {
        if (user?.companyId) {
            loadDocuments()
        }
    }, [user?.companyId, page, statusFilter])

    const loadDocuments = async () => {
        if (!user?.companyId) return
        setLoading(true)
        try {
            const response = await getSignDocuments(user.companyId, {
                page,
                limit: 15,
                status: statusFilter || undefined,
                partnerId: user?.partnerId || undefined,
            })
            if (response.success) {
                setDocuments(response.result || [])
                if (response.pagination) {
                    setPagination(response.pagination)
                }
                if (response.total !== undefined) {
                    setTotal(response.total)
                }
            } else {
                toast.error(response.error || 'Erro ao carregar documentos')
            }
        } catch {
            toast.error('Erro ao carregar documentos')
        } finally {
            setLoading(false)
        }
    }

    const handleDelete = async () => {
        if (!deleteModalId) return
        setDeleting(true)
        try {
            const response = await deleteSignDocument(deleteModalId)
            if (response.success) {
                toast.success('Documento removido com sucesso')
                loadDocuments()
            } else {
                toast.error(response.error || 'Erro ao remover documento')
            }
        } catch {
            toast.error('Erro ao remover documento')
        } finally {
            setDeleting(false)
            setDeleteModalId(null)
        }
    }

    function getSignersInfo(doc: SignDocument) {
        const signed = doc.signers?.filter(s => s.status === 'SIGNED').length || 0
        const total = doc.signers?.length || 0
        return `${signed}/${total}`
    }

    if (loading && documents.length === 0) {
        return (
            <div className="flex flex-col items-center justify-center min-h-[400px] gap-4">
                <div className="loading loading-spinner loading-lg text-secondary"></div>
                <p className="text-base-content/60 text-sm">
                    Carregando documentos...
                </p>
            </div>
        )
    }

    return (
        <div className="flex flex-col gap-6">
            {/* Header */}
            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
                <div className="flex items-center gap-4">
                    <div className="w-14 h-14 rounded-2xl bg-gradient-to-br from-secondary via-secondary/90 to-secondary/70 flex items-center justify-center shadow-lg shadow-secondary/20">
                        <MdDraw className="text-3xl text-white" />
                    </div>
                    <div>
                        <h1 className="text-2xl md:text-3xl font-bold text-base-content">
                            Assinatura Eletrônica
                        </h1>
                        <p className="text-sm md:text-base text-base-content/60 mt-1">
                            {total}{' '}
                            {total === 1
                                ? 'documento cadastrado'
                                : 'documentos cadastrados'}
                        </p>
                    </div>
                </div>
                {canCreate && (
                    <button
                        className="btn btn-secondary gap-2"
                        onClick={() => navigate('/commerce/subscriptions/new')}
                    >
                        <MdAdd className="text-lg" />
                        Novo Documento
                    </button>
                )}
            </div>

            {/* Filtros */}
            <div className="flex flex-col sm:flex-row gap-3">
                <select
                    className="select select-bordered w-full sm:w-48"
                    value={statusFilter}
                    onChange={e => {
                        setStatusFilter(e.target.value)
                        setPage(1)
                    }}
                >
                    {STATUS_FILTER_OPTIONS.map(opt => (
                        <option key={opt.value} value={opt.value}>
                            {opt.label}
                        </option>
                    ))}
                </select>
            </div>

            {/* Lista */}
            {documents.length === 0 ? (
                <div className="bg-gradient-to-br from-base-100 to-base-200/30 rounded-2xl border border-base-300/50 shadow-lg p-16 text-center">
                    <div className="w-24 h-24 mx-auto mb-6 rounded-full bg-gradient-to-br from-secondary/10 to-secondary/5 flex items-center justify-center">
                        <MdDraw className="text-5xl text-secondary/40" />
                    </div>
                    <h3 className="text-xl md:text-2xl font-bold text-base-content mb-3">
                        {statusFilter
                            ? 'Nenhum documento encontrado'
                            : 'Nenhum documento de assinatura'}
                    </h3>
                    <p className="text-base-content/60 mb-8 max-w-md mx-auto">
                        {statusFilter
                            ? 'Tente ajustar os filtros de busca'
                            : 'Documentos enviados para assinatura aparecerão aqui'}
                    </p>
                    {!statusFilter && canCreate && (
                        <button
                            className="btn btn-secondary gap-2"
                            onClick={() => navigate('/commerce/subscriptions/new')}
                        >
                            <MdAdd className="text-lg" />
                            Novo Documento
                        </button>
                    )}
                </div>
            ) : (
                <>
                    <div className="overflow-x-auto bg-base-100 rounded-2xl border border-base-300/50 shadow-sm">
                        <table className="table">
                            <thead>
                                <tr>
                                    <th>ID</th>
                                    <th>Origem</th>
                                    <th>Descrição</th>
                                    <th>Status</th>
                                    <th>Signatários</th>
                                    <th>Data</th>
                                    <th></th>
                                </tr>
                            </thead>
                            <tbody>
                                {documents.map(doc => {
                                    const statusInfo =
                                        STATUS_LABELS[doc.status] || {
                                            label: doc.status,
                                            className: 'badge-ghost',
                                        }
                                    return (
                                        <tr
                                            key={doc.id}
                                            className="hover cursor-pointer"
                                            onClick={() =>
                                                navigate(
                                                    `/commerce/subscriptions/${doc.id}`,
                                                )
                                            }
                                        >
                                            <td className="font-mono text-sm">
                                                #{doc.id}
                                            </td>
                                            <td>
                                                <span className="badge badge-sm badge-outline">
                                                    {getOriginLabel(doc.origin)}
                                                </span>
                                                {doc.originId && (
                                                    <span className="text-xs text-base-content/50 ml-1">
                                                        #{doc.originId}
                                                    </span>
                                                )}
                                            </td>
                                            <td className="max-w-[200px] truncate text-sm">
                                                {doc.description || '-'}
                                            </td>
                                            <td>
                                                <span
                                                    className={`badge badge-sm ${statusInfo.className}`}
                                                >
                                                    {statusInfo.label}
                                                </span>
                                            </td>
                                            <td className="text-sm">
                                                {getSignersInfo(doc)}
                                            </td>
                                            <td className="text-sm text-base-content/60">
                                                {format(
                                                    new Date(doc.createdAt),
                                                    'dd/MM/yyyy',
                                                    { locale: ptBR },
                                                )}
                                            </td>
                                            <td>
                                                {canDelete &&
                                                    doc.status === 'PENDING' && (
                                                        <button
                                                            className="btn btn-ghost btn-xs text-error"
                                                            onClick={e => {
                                                                e.stopPropagation()
                                                                setDeleteModalId(
                                                                    doc.id,
                                                                )
                                                            }}
                                                            title="Remover"
                                                        >
                                                            <MdDelete className="text-base" />
                                                        </button>
                                                    )}
                                            </td>
                                        </tr>
                                    )
                                })}
                            </tbody>
                        </table>
                    </div>

                    {/* Paginação */}
                    {pagination && (pagination.pages?.length || 0) > 1 && (
                        <div className="flex items-center justify-center gap-2">
                            <button
                                className="btn btn-sm btn-ghost"
                                disabled={pagination.first}
                                onClick={() => setPage(pagination.prev || 1)}
                            >
                                <MdArrowBack className="text-base" />
                            </button>
                            {pagination.pages?.map(p => (
                                <button
                                    key={p}
                                    className={`btn btn-sm ${p === pagination.page
                                            ? 'btn-secondary'
                                            : 'btn-ghost'
                                        }`}
                                    onClick={() => setPage(p)}
                                >
                                    {p}
                                </button>
                            ))}
                            <button
                                className="btn btn-sm btn-ghost"
                                disabled={pagination.end}
                                onClick={() =>
                                    setPage(pagination.next || page)
                                }
                            >
                                <MdArrowForward className="text-base" />
                            </button>
                        </div>
                    )}
                </>
            )}

            <ConfirmModal
                isOpen={!!deleteModalId}
                onClose={() => setDeleteModalId(null)}
                onConfirm={handleDelete}
                title="Confirmar Exclusão"
                message="Tem certeza que deseja remover este documento? Apenas documentos pendentes podem ser removidos."
                confirmButtonText="Remover"
                loading={deleting}
            />
        </div>
    )
}
